import apiClient from '../lib/api/client';
import { toRelatedMessage } from '../components/chat/components/ChatDetailsPanel/utils/followUpUtils';

export const FOLLOW_UP_PAGE_SIZE = 5;
// Follow-ups page (GET /api/chats/follow-ups/) page size
export const FOLLOW_UPS_MODULE_PAGE_SIZE = 30;

// Backend status codes: 0 = pending, 1 = completed, 2 = cancelled
const STATUS_BY_CODE = { 0: 'pending', 1: 'completed', 2: 'cancelled' };

/**
 * Error raised when the backend answers with `isSuccess: false` (or an unexpected payload).
 * Mirrors the axios error shape (`response.status` / `response.data`) so callers and the shared
 * toast/error extractors handle both the same way.
 */
export class FollowUpApiError extends Error {
  constructor(message, { status = null, data = null } = {}) {
    super(message);
    this.name = 'FollowUpApiError';
    this.response = { status, data };
  }
}

const unwrap = (response, fallbackMessage) => {
  const body = response?.data;
  if (!body || body.isSuccess === false || body.data == null) {
    const message = body?.error?.message || fallbackMessage;
    throw new FollowUpApiError(message, { status: response?.status ?? null, data: body });
  }
  return body.data;
};
// handle thin

const normalizeStatus = (status, statusLabel) => {
  if (typeof status === 'number') return STATUS_BY_CODE[status] || 'pending';
  const value = String(status || statusLabel || '').toLowerCase();
  return ['pending', 'completed', 'cancelled'].includes(value) ? value : 'pending';
};

const userName = (user) => user?.name || user?.email || 'Unknown User';

// The web API sends snake_case + ISO dates; some endpoints (e.g. restore) send the mobile shape:
// camelCase + epoch milliseconds. Read either.
const pick = (item, snake, camel) => (item[snake] !== undefined ? item[snake] : item[camel]);
const toIso = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return new Date(value).toISOString();
  return String(value);
};

/**
 * The API has no activity log endpoint yet, so the timeline is derived from the follow-up's
 * own audit fields (created / last edited / completed).
 */
const deriveActivityLogs = (item) => {
  const logs = [];
  if (item.created_at) {
    logs.push({ id: `${item.id}-created`, action: 'created', user_name: userName(item.created_by), created_at: item.created_at });
  }
  if (item.is_edit && item.updated_at && item.updated_at !== item.completed_at && item.updated_at !== item.created_at) {
    logs.push({ id: `${item.id}-edited`, action: 'edited', user_name: userName(item.created_by), created_at: item.updated_at });
  }
  if (item.completed_at) {
    logs.push({ id: `${item.id}-completed`, action: 'completed', user_name: userName(item.completed_by), created_at: item.completed_at });
  }
  return logs.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
};

export const normalizeFollowUp = (raw) => {
  const item = {
    id: raw.id,
    conversation_id: pick(raw, 'conversation_id', 'conversationId') ?? null,
    title: raw.title || '',
    description: raw.description || '',
    due_at: toIso(pick(raw, 'due_at', 'dueAt')),
    status: normalizeStatus(raw.status, pick(raw, 'status_label', 'statusLabel')),
    status_label: pick(raw, 'status_label', 'statusLabel') || '',
    is_due: !!pick(raw, 'is_due', 'isDue'),
    is_upcoming: !!pick(raw, 'is_upcoming', 'isUpcoming'),
    is_deleted: !!pick(raw, 'is_deleted', 'isDeleted'),
    related_message: toRelatedMessage(raw.message),
    created_by: pick(raw, 'created_by', 'createdBy') || null,
    completed_by: pick(raw, 'completed_by', 'completedBy') || null,
    completed_at: toIso(pick(raw, 'completed_at', 'completedAt')),
    created_at: toIso(pick(raw, 'created_at', 'createdAt')),
    updated_at: toIso(pick(raw, 'updated_at', 'updatedAt')),
    is_edit: !!(pick(raw, 'is_edit', 'isEdited') ?? raw.isEdit),
  };
  // No "trashed at" field: the last update of a trashed follow-up is its trash time.
  item.deleted_at = item.is_deleted ? item.updated_at || item.created_at : null;
  item.activity_logs = deriveActivityLogs(item);
  return item;
};

const toPage = (data) => {
  const pagination = data.pagination || {};
  return {
    followUps: (Array.isArray(data.follow_ups) ? data.follow_ups : []).map(normalizeFollowUp),
    nextCursor: pagination.has_more && pagination.next_cursor ? pagination.next_cursor : null,
    hasMore: !!pagination.has_more && !!pagination.next_cursor,
  };
};

/**
 * GET /api/chats/conversations/{conversation_id}/follow-ups/
 * @returns {{ followUps: object[], nextCursor: string|null, hasMore: boolean }}
 */
export const getConversationFollowUps = async (
  conversationId,
  { search = '', cursor = null, limit = FOLLOW_UP_PAGE_SIZE, signal } = {}
) => {
  const params = { limit };
  if (search) params.search = search;
  if (cursor) params.cursor = cursor;

  const response = await apiClient.get(`/api/chats/conversations/${conversationId}/follow-ups/`, { params, signal });
  return toPage(unwrap(response, 'Failed to load follow-ups.'));
};

/**
 * GET /api/chats/follow-ups/ — tenant-wide list (Follow-ups page).
 * @param {object} filters API filters, e.g. `{ status: 'pending', due_date_to }`, `{ completed: true }`,
 *   `{ trashed: true }`. Never send `cursor` together with `last_followup_id`.
 */
export const getFollowUps = async ({ filters = {}, search = '', cursor = null, limit = FOLLOW_UPS_MODULE_PAGE_SIZE } = {}) => {
  const params = { ...filters, limit };
  if (search) params.search = search;
  if (cursor) params.cursor = cursor;

  const response = await apiClient.get('/api/chats/follow-ups/', { params });
  return toPage(unwrap(response, 'Failed to load follow-ups.'));
};

/**
 * POST /api/chats/follow-ups/{follow_up_id}/restore/ — takes a follow-up out of the trash.
 * Restoring one that isn't trashed is a safe no-op (200 with the current object).
 * Note: this endpoint answers with the mobile shape (`message` at top level, camelCase `data`).
 */
export const restoreFollowUp = async (followUpId) => {
  const response = await apiClient.post(`/api/chats/follow-ups/${followUpId}/restore/`);
  const body = response?.data;
  const data = unwrap(response, 'Failed to restore follow-up.');
  const raw = data.follow_up || data.followUp || (data.id != null ? data : null);
  return {
    followUp: raw ? normalizeFollowUp(raw) : null,
    message: body?.message || data.message || null,
  };
};

export const FOLLOW_UP_ACTIVITY_LOGS_PAGE_SIZE = 20;

// Backend ActionType → timeline style key (see LOG_STYLES in FollowUpSheet)
const ACTIVITY_ACTION_KEYS = {
  CREATE: 'created',
  UPDATE: 'edited',
  STATUS_CHANGE: 'status_changed',
  DELETE: 'trashed',
  RESTORE: 'restored',
  DUE: 'due',
  CANCELLED: 'cancelled',
};

// STATUS_CHANGE stores the changed fields in old_value → new_value; `new_value.status` is the
// status that was set (0 Pending, 1 Completed, 2 Cancelled)
const statusChangeKey = (log) => {
  const value = log.new_value?.status ?? log.newValue?.status;
  if (value == null) return 'status_changed';
  const status = normalizeStatus(typeof value === 'number' || Number.isNaN(Number(value)) ? value : Number(value));
  return status === 'completed' ? 'completed' : status === 'pending' ? 'reopened' : status === 'cancelled' ? 'cancelled' : 'status_changed';
};

// Fields a user can change (UPDATE, or content changed in the same PATCH as a STATUS_CHANGE)
const CHANGED_FIELD_LABELS = {
  title: 'Title',
  description: 'Description',
  due_at: 'Due date',
  message_id: 'Related message',
};

const describeChangedFields = (log) => {
  const changed = log.new_value || log.newValue;
  if (!changed || typeof changed !== 'object') return null;
  const labels = Object.keys(changed).map((key) => CHANGED_FIELD_LABELS[key]).filter(Boolean);
  return labels.length ? `Changed: ${labels.join(', ')}` : null;
};

// Related message as stored in a log value: a message object / text when the API includes it,
// otherwise only `message_id`
const MESSAGE_VALUE_KEYS = ['message', 'related_message', 'message_content', 'message_body', 'message_text'];

const readLogMessage = (value) => {
  const key = MESSAGE_VALUE_KEYS.find((name) => value[name] !== undefined);
  if (key) {
    const message = value[key];
    if (message == null || message === '') return { type: 'empty' };
    if (typeof message === 'object') return { type: 'message', message: toRelatedMessage(message) };
    return { type: 'text', text: String(message) };
  }
  if (value.message_id === undefined) return undefined;
  return value.message_id == null ? { type: 'empty' } : { type: 'id', id: value.message_id };
};

const hasMessageValue = (value) =>
  !!value && (value.message_id !== undefined || MESSAGE_VALUE_KEYS.some((name) => value[name] !== undefined));

// Fields shown in "View Changes" (Before / After), in this order
const CHANGE_FIELDS = [
  { key: 'title', label: 'Title', kind: 'text', read: (value) => value.title },
  { key: 'description', label: 'Description', kind: 'text', read: (value) => value.description },
  { key: 'message', label: 'Related Message', kind: 'message', has: hasMessageValue, read: readLogMessage },
  { key: 'due_at', label: 'Date & Time', kind: 'date', read: (value) => toIso(pick(value, 'due_at', 'dueAt')) },
];

const sameChangeValue = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * Before / After pairs of the fields an UPDATE (or a STATUS_CHANGE that also edited content)
 * changed, from the log's old_value → new_value. Fields that didn't change are left out.
 */
const getLogChanges = (log) => {
  const before = pick(log, 'old_value', 'oldValue');
  const after = pick(log, 'new_value', 'newValue');
  if (!after || typeof after !== 'object') return [];
  const oldValue = before && typeof before === 'object' ? before : {};

  return CHANGE_FIELDS.reduce((changes, field) => {
    const inNew = field.has ? field.has(after) : field.read(after) !== undefined;
    if (!inNew) return changes;
    const from = field.has ? (field.has(oldValue) ? field.read(oldValue) : undefined) : field.read(oldValue);
    const to = field.read(after);
    if (sameChangeValue(from, to)) return changes;
    changes.push({ key: field.key, label: field.label, kind: field.kind, before: from ?? null, after: to ?? null });
    return changes;
  }, []);
};

const toTitleCase = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

/**
 * One activity-log row → timeline entry. Fields: id, action_type, performed_by(_name/_email),
 * old_value / new_value, created_at, metadata.source (web_api | mobile_api | scheduler).
 */
export const normalizeActivityLog = (log, index = 0) => {
  const actionType = String(pick(log, 'action_type', 'actionType') || log.action || '').toUpperCase();
  const action = actionType === 'STATUS_CHANGE' ? statusChangeKey(log) : ACTIVITY_ACTION_KEYS[actionType] || null;
  // DUE is set by the scheduler (performed_by null); a deleted user also has no name/email
  const userName =
    pick(log, 'performed_by_name', 'performedByName') ||
    pick(log, 'performed_by_email', 'performedByEmail') ||
    'System';
  return {
    id: log.id ?? `${actionType}-${index}-${pick(log, 'created_at', 'createdAt')}`,
    action_type: actionType,
    action,
    // Unknown action types still render, labelled from the raw type (e.g. "SOME_ACTION" → "Some Action")
    label: action ? null : toTitleCase(actionType) || 'Activity',
    detail: actionType === 'UPDATE' || actionType === 'STATUS_CHANGE' ? describeChangedFields(log) : null,
    changes: actionType === 'UPDATE' || actionType === 'STATUS_CHANGE' ? getLogChanges(log) : [],
    user_name: userName,
    created_at: toIso(pick(log, 'created_at', 'createdAt')),
  };
};

/**
 * GET /api/chats/follow-ups/{follow_up_id}/activity-logs/?page=&page_size= — page-numbered.
 * Only documented params may be sent (unknown ones → 400). Optional filters the API supports:
 * action_performed, performed_by, date_from, date_to (YYYY-MM-DD).
 * @returns {{ logs: object[], hasNext: boolean }}
 */
export const getFollowUpActivityLogs = async (
  followUpId,
  { page = 1, pageSize = FOLLOW_UP_ACTIVITY_LOGS_PAGE_SIZE, filters = {} } = {}
) => {
  const response = await apiClient.get(`/api/chats/follow-ups/${followUpId}/activity-logs/`, {
    params: { ...filters, page, page_size: pageSize },
  });
  const data = unwrap(response, 'Failed to load activity logs.');
  const rows = Array.isArray(data)
    ? data
    : data.activity_logs || data.activityLogs || data.results || data.logs || data.items || [];
  const pagination = data.pagination || {};
  // `next` may be a boolean or the next page's URL
  const next = data.next ?? pagination.next ?? data.has_next ?? pagination.has_next ?? pagination.has_more;
  return {
    logs: (Array.isArray(rows) ? rows : []).map((log, index) => normalizeActivityLog(log, (page - 1) * pageSize + index)),
    hasNext: !!next,
  };
};

/**
 * GET /api/chats/conversation/{id} — conversation summary (customer name, channel, status) for
 * follow-ups whose conversation isn't loaded in the chat store yet.
 */
export const getConversationDetail = async (conversationId) => {
  const response = await apiClient.get(`/api/chats/conversation/${conversationId}`);
  const payload = response?.data;
  // Tolerate both the wrapped ({ isSuccess, data }) and raw conversation shapes
  const detail = payload?.data ?? (payload && payload.id != null ? payload : null);
  if (!detail || detail.id == null) {
    throw new FollowUpApiError(payload?.error?.message || 'Conversation not found', { status: response?.status ?? null, data: payload });
  }
  return detail;
};

/**
 * POST /api/chats/follow-ups/
 * @param {{ conversation_id: number, message_id: number|null, title: string, description: string, due_at: string }} payload
 */
export const createFollowUp = async (payload) => {
  const response = await apiClient.post('/api/chats/follow-ups/', payload);
  const data = unwrap(response, 'Failed to create follow-up.');
  const created = data.follow_up || (Array.isArray(data.follow_ups) ? data.follow_ups[0] : null);
  return created ? normalizeFollowUp(created) : null;
};

/**
 * PATCH /api/chats/follow-ups/{follow_up_id}/ — send only the fields that changed.
 * @param {{ title?: string, description?: string, due_at?: string, message_id?: number|null, status?: 'pending'|'completed'|'cancelled' }} changes
 */
export const updateFollowUp = async (followUpId, changes) => {
  const response = await apiClient.patch(`/api/chats/follow-ups/${followUpId}/`, changes);
  const data = unwrap(response, 'Failed to update follow-up.');
  return {
    followUp: data.follow_up ? normalizeFollowUp(data.follow_up) : null,
    message: data.message || null,
  };
};

/**
 * POST /api/chats/follow-ups/{follow_up_id}/trash/ — soft delete (can be restored later).
 */
export const trashFollowUp = async (followUpId) => {
  const response = await apiClient.post(`/api/chats/follow-ups/${followUpId}/trash/`);
  const data = unwrap(response, 'Failed to move follow-up to trash.');
  return {
    followUp: data.follow_up ? normalizeFollowUp(data.follow_up) : null,
    message: data.message || null,
  };
};
